import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import useLinkAfterCreate from 'src/Components/EntityLinks/hooks/useLinkAfterCreate';
import PhysicalContactForm from 'src/Components/Forms/Contact/PhysicalContactForm/PhysicalContactForm.component';
import { useCreatePhysicalContactForm } from 'src/Hooks/Forms/useCreatePhysicalContactForm';
import useSiretConflictHandler from 'src/Hooks/Modals/useSiretConflictHandler';
import { useCreatePhysicalContact } from 'src/Hooks/Mutations/useCreateContact';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { ContactKind } from 'src/Services/API/generated';
import { trackContactCompanyCreated } from 'src/Services/Mixpanel/events';

import { Props } from './ContactCreationFormWrapper.types';

const ContactCreationFormWrapper = ({ onCreate, onCancel }: Props) => {
  const { form } = useCreatePhysicalContactForm();
  const { handleCreated } = useLinkAfterCreate(LinkableEntityType.Contact);
  const handleSiretConflict = useSiretConflictHandler();
  const [searchParams] = useSearchParams();
  const prefillCompanyName = searchParams.get('prefillCompanyName');

  // ?prefillCompanyName= (creating a supplier from elsewhere): company mode, search running.
  useEffect(() => {
    if (!prefillCompanyName) return;
    form.setFieldValue('kind', ContactKind.Company);
    form.setFieldValue('kindTouched', true);
    form.setFieldValue('companyName', prefillCompanyName);
  }, [prefillCompanyName]);

  const { mutate, isLoading } = useCreatePhysicalContact({
    onError: error => {
      handleSiretConflict(error);
    },
    onSuccess: (data, variables) => {
      if (variables.kind === ContactKind.Company) {
        trackContactCompanyCreated({
          interlocutors_count: variables.interlocutors?.length ?? 0,
          has_siret: !!variables.siret,
          has_usual_payment_mode: !!variables.usualPaymentMode,
        });
      }
      if (form.values.addAnother) {
        form.reset();
      } else {
        handleCreated(data?.id, () => onCreate(data?.id));
      }
    },
  });
  const { t } = useTranslation();
  const { data: membersData } = useFetchMembers();

  /**
   * A3 — a contact homonymous with a co-owner is a trap: picked by mistake as the payer of
   * an expense, it makes that expense invisible to the balances. Warn, never block: a real
   * namesake contact is legitimate.
   */
  const homonymMember = (values: { firstName?: string; lastName?: string }) => {
    const name = `${values.firstName ?? ''} ${values.lastName ?? ''}`.trim().toLowerCase();
    if (!name) return undefined;
    return (membersData?.members ?? []).find(
      member =>
        `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim().toLowerCase() === name
    );
  };

  return (
    <PhysicalContactForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(values => {
        // A company cannot be mistaken for a co-owner.
        const member = values.kind === ContactKind.Person ? homonymMember(values) : undefined;
        if (!member) {
          mutate(values);
          return;
        }

        modals.openConfirmModal({
          title: t('createContactForm.homonymTitle', 'Un copropriétaire porte déjà ce nom'),
          children: (
            <Text size="sm">
              {t(
                'createContactForm.homonymBody',
                '{{name}} est déjà copropriétaire de ce bien. Un contact du même nom ne peut pas être remboursé par les copropriétaires. Voulez-vous vraiment créer ce contact ?',
                { name: `${member.user.firstName} ${member.user.lastName}` }
              )}
            </Text>
          ),
          labels: {
            confirm: t('createContactForm.homonymConfirm', 'Créer quand même'),
            cancel: t('common.cancel', 'Annuler'),
          },
          onConfirm: () => mutate(values),
        });
      })}
      submitButtonLabel={t('createContactForm.addContact', 'Ajouter')}
      submitAndAddAnotherButtonLabel={t(
        'contacts.saveAndAddAnother',
        'Enregistrer et ajouter un autre'
      )}
      loading={isLoading}
      isCreation
      autoFocusCompanyName={!!prefillCompanyName}
    />
  );
};

export default ContactCreationFormWrapper;
